import React, { useState, useEffect } from "react";
import { Search, Building2, CheckCircle, XCircle, FileText } from "lucide-react";
import { UserService } from "../../services/users/userService";
import { FournisseurService } from "../../services/fournisseurs/fournisseurService";
import { toast } from "react-hot-toast";
import { DocumentViewerModal } from "../../components/common/Modals/DocumentViewerModal";
import { useTranslation } from "react-i18next";

interface Document {
  id: string;
  type_document: string;
  fichier: string;
  nom_fichier: string;
}

interface FournisseurData {
  id: string;
  user_id: number;
  user_email: string;
  raison_sociale: string;
  siret: string;
  iban: string;
  is_verified: boolean;
  documents: Document[];
  statut: string;
}

export const AdminFournisseurs: React.FC = () => {
  const { t } = useTranslation();
  const [fournisseurs, setFournisseurs] = useState<FournisseurData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [validatingId, setValidatingId] = useState<number | null>(null);
  const [viewingDocument, setViewingDocument] = useState<{url: string, title: string} | null>(null);

  const fetchFournisseurs = async () => {
    try {
      setLoading(true);
      const response = await FournisseurService.getFournisseurs();
      setFournisseurs(response.data.results || response.data || []);
    } catch (error) {
      console.error("Erreur chargement fournisseurs", error);
      toast.error(t('adminFournisseurs.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFournisseurs();
  }, []);

  const handleValidate = async (userId: number, frId: string) => {
    try {
      setValidatingId(userId);
      await UserService.validateFournisseur(userId.toString());
      toast.success(t('adminFournisseurs.messages.success'));
      setFournisseurs(fournisseurs.map(f => f.id === frId ? { ...f, is_verified: true, statut: 'actif' } : f));
    } catch (error) {
      console.error(error);
      toast.error(t('adminFournisseurs.messages.error'));
    } finally {
      setValidatingId(null);
    }
  };

  const filteredFournisseurs = fournisseurs.filter((f) => 
    f.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.raison_sociale?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('adminFournisseurs.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {t('adminFournisseurs.overview')}
          </p>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="p-4 border-b border-gray-200">
          <div className="relative max-w-xs">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
              placeholder={t('header.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('adminFournisseurs.table.supplier')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('adminFournisseurs.table.contact')}
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('adminFournisseurs.table.kyc')}
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('adminFournisseurs.table.status')}
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                 <tr>
                   <td colSpan={5} className="px-6 py-4 text-center">
                     <div className="flex justify-center">
                       <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                     </div>
                   </td>
                 </tr>
              ) : filteredFournisseurs.length === 0 ? (
                <tr>
                   <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                     Aucun fournisseur trouvé.
                   </td>
                 </tr>
              ) : (
                filteredFournisseurs.map((fournisseur) => (
                  <tr key={fournisseur.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-orange-100 rounded-full flex items-center justify-center">
                          <Building2 className="h-6 w-6 text-orange-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {fournisseur.raison_sociale || t('adminFournisseurs.notRenseigne')}
                          </div>
                          <div className="text-xs text-gray-400">{fournisseur.user_email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 outline-none">
                      <div className="text-sm text-gray-900">
                        {fournisseur.iban ? (
                           <span className="font-mono text-xs">IBAN: {fournisseur.iban}</span>
                        ): <span className="text-gray-400 italic">{t('adminFournisseurs.pendingIban')}</span>}
                      </div>
                      <div className="text-sm text-gray-500">
                         Siret: {fournisseur.siret || "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                        {fournisseur.documents && fournisseur.documents.length > 0 ? (
                           <div className="flex justify-center flex-wrap gap-2">
                               {fournisseur.documents.map((doc, idx) => (
                                  <button 
                                      key={idx} 
                                      onClick={() => setViewingDocument({ url: doc.fichier, title: doc.nom_fichier })}
                                      className="flex items-center text-xs text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded" 
                                      title={doc.nom_fichier}
                                  >
                                      <FileText className="h-4 w-4 mr-1" />
                                      {doc.type_document}
                                  </button>
                               ))}
                            </div>
                        ) : (
                            <span className="text-xs text-gray-400 uppercase">{t('adminFournisseurs.noFile')}</span>
                        )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                       {fournisseur.is_verified ? (
                           <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                               <CheckCircle className="h-4 w-4 mr-1" /> {t('users.table.verified')}
                           </span>
                       ) : (
                           <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                               <XCircle className="h-4 w-4 mr-1" /> {t('clients.status.pending')}
                           </span>
                       )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {!fournisseur.is_verified && (
                        <button 
                           onClick={() => handleValidate(fournisseur.user_id, fournisseur.id)}
                           disabled={validatingId === fournisseur.user_id}
                           className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                        >
                           {validatingId === fournisseur.user_id ? t('adminFournisseurs.actions.validating') : t('adminFournisseurs.actions.validate')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {viewingDocument && (
        <DocumentViewerModal
            url={viewingDocument.url}
            title={viewingDocument.title}
            onClose={() => setViewingDocument(null)}
        />
      )}
    </div>
  );
};
